<template>
  <Transition name="possibly-offline">
    <div
      v-if="possiblyOffline && !dismissNotification"
      id="maybe-offline-mode-sticky"
      class="bg-warning"
    >
      <font-awesome-icon icon="wifi" class="mr-2 anim-pulse" />
      It looks like you might be offline or having network issues.
      <button
        class="btn btn-secondary btn-sm"
        @click="
          $emit('go-to-offline-check');
          possiblyOffline = false;
        "
      >
        <font-awesome-icon icon="arrow-right" class="mr-2" /> Check my
        connection
      </button>
      <button
        class="btn btn-outline-secondary btn-sm ms-2"
        @click="dismissNotification = true"
      >
        <font-awesome-icon icon="times" class="mr-2" />
        Dismiss
      </button>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { Hub } from "aws-amplify/utils";
import { useAuthStore } from "@switchsystems/coactive-vue-lib";
import debounce from "lodash/debounce";

defineEmits(["go-to-offline-check"]);

const possiblyOffline = ref(false);
const dismissNotification = ref(false);
const authStore = useAuthStore();

const doDebouncedCheck = debounce(async () => {
  const result = await authStore.checkAuthApiStatus();
  if (result !== true) {
    possiblyOffline.value = true;
  }
}, 1000);

/**
 * Listen to the toast hub for API network errors
 */
Hub.listen("toast", ({ payload }) => {
  if (payload.source === "api") {
    // if we get an API error, do a ping check to see if we're online.
    if (!dismissNotification.value) {
      doDebouncedCheck();
    }
  }
});

const onWindowOffline = () => {
  if (!dismissNotification.value) {
    doDebouncedCheck();
  }
};

/**
 * Listen to browser offline event
 */
onMounted(() => {
  window.addEventListener("offline", onWindowOffline);
});

onBeforeUnmount(() => {
  window.removeEventListener("offline", onWindowOffline);
});
</script>

<style>
div#maybe-offline-mode-sticky {
  border-top-style: solid;
  border-top-width: 10px;
  border-top-color: #e7960b;
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 9999;
  opacity: 0.95;
  padding: 4px;
  width: 100%;
  text-align: center;
  transition: all 1s ease;
}
.possibly-offline-enter-active,
.possibly-offline-leave-active {
  transition: opacity 1s ease;
}

.possibly-offline-enter-from,
.possibly-offline-leave-to {
  opacity: 0 !important;
}
</style>
